import { MNHotInstance } from '../../../tableHotInstanceTypes';
import {
    getDefaultTableCellLineHeight,
    getDefaultTableCellVerticalPadding,
} from '../grid/hotTableGridDefaultDimensionUtils';

const BORDER_PX = 1;

const getEditorVerticalBordersWidth = () => BORDER_PX * 2;

/**
 * The height of cell renderer include the border it owns.
 * - First row owns its top and bottom border
 * - Other rows own its bottom border
 */
const getRendererVerticalBordersWidth = (row: number) => {
    const topBorderHeight = row === 0 ? BORDER_PX : 0;
    const bottomBorderHeight = BORDER_PX;

    return topBorderHeight + bottomBorderHeight;
};

/**
 * Get cell renderer height from it's respective cell editor height
 */
export const toRendererHeight = (editorHeight: number, row: number) => {
    const cellHeightWithoutBorders = editorHeight - getEditorVerticalBordersWidth();
    return cellHeightWithoutBorders + getRendererVerticalBordersWidth(row);
};

/**
 * Get cell editor height from it's respective cell editor height
 */
export const toEditorHeight = (rendererHeight: number, row: number) => {
    const cellHeightWithoutBorders = rendererHeight - getRendererVerticalBordersWidth(row);
    return cellHeightWithoutBorders + getEditorVerticalBordersWidth();
};

/**
 * In order to make table fit into Milanote's grid, some adjustments are made
 * to cell padding on the first and last row.
 *
 * This logic is also present in CSS HotTableRowHeight.scss
 */
export const getTableCellVerticalPadding = (
    row: number,
    nRows: number,
    props: { gridSize: number; showTitle: boolean; showBottomPanel: boolean },
) => {
    let cellVerticalPadding = getDefaultTableCellVerticalPadding(props.gridSize);

    if (!props.showTitle && row === 0) {
        cellVerticalPadding -= BORDER_PX;
    }
    if (!props.showBottomPanel && row === nRows - 1) {
        cellVerticalPadding -= BORDER_PX * 0.5;
    }

    return cellVerticalPadding;
};

/**
 * Get min height for the editor, which is the max height of other cells in the row
 */
export const getEditorMinHeight = (hotInstance: MNHotInstance, row: number, col: number) => {
    const { gridSize } = hotInstance.milanoteProps;

    const nRows = hotInstance.countRows();
    const nCols = hotInstance.countCols();

    // Iterate over the columns in this row, skipping the current column
    const rowCellContentHeights = Array.from({ length: nCols }, (_, index) => index)
        .filter((otherCol) => otherCol !== col)
        .map((otherCol) => {
            const cell = hotInstance.getCell(row, otherCol);
            return (cell?.firstChild as HTMLElement)?.offsetHeight || 0;
        });

    const verticalPadding = getTableCellVerticalPadding(row, nRows, hotInstance.milanoteProps);
    const verticalBordersWidth = getRendererVerticalBordersWidth(row);

    const defaultCellContentHeight = getDefaultTableCellLineHeight(gridSize);

    const maxContentHeight = Math.max(defaultCellContentHeight, ...rowCellContentHeights);
    const maxCellRendererHeight = maxContentHeight + verticalPadding * 2 + verticalBordersWidth;

    return toEditorHeight(maxCellRendererHeight, row);
};
